import { EmploymentField, useEmploymentSelection } from './EmploymentField'
import { useMemo, useState } from 'react'
import { Text, XStack, YStack } from 'tamagui'

import { useApp, type Localized } from '../../../i18n'
import { tone } from '../../../theme/tones'
import { Chip, DateField, FormTextArea, Icon, TimeField, monthStartYmd } from '../../../ui'
import { TIME_RE, hmToMin, minToHm, toIsoUtc, ymdPlusDays } from '../../../lib/date'
import { fmtNum } from '../../../lib/format'
import type { ProductionDay, Profile } from '../../../lib/types'
import { useCreateTimeOff, useTimeOffBalance, useTimeOffReasons } from '../useEdo'
import { useTimeOffProductionDays } from '../../calendar/useCalendar'
import {
  evaluateTimeOff,
  windowFor,
  fmtHm,
  type EvalCode,
  type WorkdayWindow,
} from '../timeOffHours'
import { RoutePreview } from '../components/RouteTimeline'
import { Field, FormActions, MISSING_WORKPLACE, dateCol, timeCol } from './shared'
import { OnBehalfField } from './OnBehalfField'

// ── Time-off helpers (ТЗ-2026-002) ──────────────────────────────────────────────
/** Fallback reasons if GET /edo/time-off/reasons is slow/unreachable (mirrors the 1С list). */
const TIME_OFF_REASONS_FALLBACK = [
  'Медосмотр/здоровье', 'Семейные обстоятельства', 'Госорганы', 'Банк/нотариус', 'Другое',
]
/** Код ошибки окна отгула → локализованная подсказка (границы окна берём из графика дня). */
function evalHint(code: EvalCode, w: WorkdayWindow): Localized {
  switch (code) {
    case 'ORDER':
      return { ru: 'Время окончания должно быть позже начала', en: 'End time must be after the start' }
    case 'BEFORE_START':
      return {
        ru: `Начало отгула не раньше ${fmtHm(w.startMin)}`,
        en: `Start no earlier than ${fmtHm(w.startMin)}`,
        az: `İcazə ən tezi ${fmtHm(w.startMin)}-da başlaya bilər`,
      }
    case 'AFTER_END':
      return {
        ru: `Окончание отгула не позже ${fmtHm(w.endMin)}`,
        en: `End no later than ${fmtHm(w.endMin)}`,
        az: `İcazə ən geci ${fmtHm(w.endMin)}-da bitməlidir`,
      }
    case 'EMPTY':
      return {
        ru: 'Отгул не может приходиться только на обеденный перерыв',
        en: 'Time-off cannot fall within the lunch break only',
      }
  }
}

export function TimeOffForm({ onDone }: { onDone: () => void }) {
  const { t, td } = useApp()
  const reasonsQ = useTimeOffReasons()
  const create = useCreateTimeOff(onDone)

  const [fromDate, setFromDate] = useState('')
  const [fromTime, setFromTime] = useState('09:00')
  const [toDate, setToDate] = useState('')
  const [toTime, setToTime] = useState('13:00')
  const [reason, setReason] = useState('')
  const [comment, setComment] = useState('')
  // Подача «от имени» (роль proxy): выбранный сотрудник станет инициатором заявки на бэке.
  const [onBehalf, setOnBehalf] = useState<Profile | null>(null)
  const employment = useEmploymentSelection(onBehalf?.id)

  // Лимит отгулов помесячный: спрашиваем остаток МЕСЯЦА ВЫБРАННОЙ ДАТЫ (у следующего месяца
  // свой лимит), без даты — текущий месяц. Пока грузится остаток нового месяца, balance
  // undefined → блокировки нет, истину всё равно проверит бэк при подаче.
  // Остаток относится к выбранному сотруднику и его месту работы.
  const balanceQ = useTimeOffBalance(fromDate ? fromDate.slice(0, 7) : undefined, employment.employmentId, onBehalf?.id, employment.ready)

  const reasons = reasonsQ.data ?? TIME_OFF_REASONS_FALLBACK
  const balance = balanceQ.data
  const remaining = balance?.limitHoursRemaining
  const limitExhausted = remaining === 0

  // Производственный календарь с 1-го числа месяца (дату можно взять задним числом в пределах
  // месяца) на год вперёд — 365 дней от 1-го числа: бэк принимает диапазон не больше 366 дней.
  // Гасим нерабочие дни в пикере (треб.1) и берём workHours выбранного дня (8 обычный /
  // 7 предпраздничный) для окна и вычета обеда (треб.2/3).
  const prodQ = useTimeOffProductionDays(monthStartYmd(), ymdPlusDays(monthStartYmd(), 365), employment.employmentId, onBehalf?.id, employment.ready)
  const prodByDay = useMemo(() => {
    const m = new Map<string, ProductionDay>()
    for (const d of (employment.ready ? prodQ.data : undefined) ?? []) m.set(d.date, d)
    return m
  }, [prodQ.data, employment.ready])
  const selectedDay = fromDate ? prodByDay.get(fromDate) : undefined
  const dayNonWorking = selectedDay ? !selectedDay.isWorkingDay : false
  const win = windowFor(selectedDay?.workHours ?? 8)
  const endHour = Math.floor(win.endMin / 60) // 18 обычный / 17 предпраздничный

  // Build ISO-UTC stamps from the local date+time inputs (server recomputes authoritatively).
  const fromISO = toIsoUtc(fromDate, fromTime)
  const toISO = toIsoUtc(toDate, toTime)
  const hasDates = !!fromISO && !!toISO

  // Длительность и допустимость времени — через общее ядро: окно дня + вычет обеда 13:00–14:00.
  const timeParsed = TIME_RE.test(fromTime) && TIME_RE.test(toTime)
  const evalRes = timeParsed ? evaluateTimeOff(hmToMin(fromTime), hmToMin(toTime), win) : null
  const duration = evalRes?.ok ? evalRes.hours : NaN
  const timeValid = !!evalRes?.ok
  const balanceOk = remaining == null || (timeValid && duration <= remaining)
  // Пустые обязательные — списком под кнопкой; ошибки значения (нерабочий день, окно, лимит)
  // подписаны своей красной строкой ниже.
  const missing = [
    !employment.ready && t(MISSING_WORKPLACE),
    !fromDate && t({ ru: 'Дата начала', en: 'Start date' }),
    !toDate && t({ ru: 'Дата окончания', en: 'End date' }),
    reason === '' && t({ ru: 'Причина', en: 'Reason' }),
  ].filter((x): x is string => !!x)
  const ok = missing.length === 0 && prodQ.isSuccess && !prodQ.isFetching &&
    hasDates && !dayNonWorking && timeValid && balanceOk &&
    comment.length <= 500 && !limitExhausted && !create.isPending

  const submit = () =>
    ok &&
    create.mutate({
      timeFrom: fromISO!,
      timeTo: toISO!,
      absenceReason: reason,
      comment: comment.trim() || undefined,
      onBehalfOfUserId: onBehalf?.id,
        employmentId: employment.employmentId,
    })

  // Отгул — один день, поэтому конец не может быть раньше/равен началу.
  // При выборе времени НАЧАЛА: если конец оказался ≤ начала — двигаем его на
  // +1 ч (с потолком 18:59). Уже валидный (более поздний) конец не трогаем.
  const onFromTime = (next: string) => {
    setFromTime(next)
    const start = hmToMin(next)
    if (hmToMin(toTime) <= start) setToTime(minToHm(Math.min(start + 60, win.endMin)))
  }

  // First failing client-side rule → inline hint (server `message` wins on submit error).
  const hint = !hasDates
    ? ''
    : dayNonWorking
      ? t({
          ru: 'Выбранный день — нерабочий (выходной или праздник)',
          en: 'Selected day is non-working (weekend or holiday)',
        })
      : !evalRes
        ? ''
        : !evalRes.ok
          ? t(evalHint(evalRes.code, win))
          : !balanceOk
            ? t({
                ru: `Запрошено ${fmtNum(duration)} ч, остаток лимита ${fmtNum(remaining!)} ч`,
                en: `Requested ${fmtNum(duration)} h, ${fmtNum(remaining!)} h left`,
                az: `Tələb olunan ${fmtNum(duration)} saat, qalan limit ${fmtNum(remaining!)} saat`,
              })
            : ''

  return (
    <YStack gap={14}>
      <OnBehalfField value={onBehalf} onChange={setOnBehalf} />
      <EmploymentField selection={employment} />
      {/* Monthly limit for the selected owner and workplace. */}
      {balance && (
        <XStack
          alignItems="center"
          gap={8}
          backgroundColor={limitExhausted ? tone('red').soft : '$primaryBg'}
          borderWidth={1}
          borderColor={limitExhausted ? tone('red').solid : '$primary'}
          borderRadius={7}
          paddingHorizontal={12}
          paddingVertical={9}
        >
          <Icon name={limitExhausted ? 'triangle-alert' : 'clock'} size={15} color={limitExhausted ? tone('red').text : '$primary'} />
          <Text fontSize={12} color="$color" flex={1}>
            {limitExhausted
              ? t({
                  ru: `Лимит на ${balance.period} исчерпан`,
                  en: `Limit for ${balance.period} exhausted`,
                  az: `${balance.period} üçün limit bitib`,
                })
              : t({
                  ru: `Остаток лимита: ${fmtNum(remaining!)} из ${fmtNum(balance.limitHoursTotal)} ч (${balance.period})`,
                  en: `Limit left: ${fmtNum(remaining!)} of ${fmtNum(balance.limitHoursTotal)} h (${balance.period})`,
                  az: `Qalan limit: ${fmtNum(remaining!)} / ${fmtNum(balance.limitHoursTotal)} saat (${balance.period})`,
                })}
          </Text>
        </XStack>
      )}

      {/* start date + time */}
      <XStack gap={12} flexWrap="wrap">
        <YStack {...dateCol} gap={6}>
          <Field label={t({ ru: 'Дата начала', en: 'Start date' })} required>
            <DateField
              value={fromDate}
              onChange={(d) => {
                // Отгул — один день: дата окончания всегда повторяет дату начала.
                setFromDate(d)
                setToDate(d)
              }}
              placeholder="ГГГГ-ММ-ДД"
              highlight={!fromDate}
              // Треб.1: гасим выходные/праздники в пикере. Дни вне загруженного календаря
              // (или пока данные грузятся) не блокируем — сервер проверит повторно.
              isDayDisabled={(day) => {
                const pd = prodByDay.get(day)
                return pd ? !pd.isWorkingDay : false
              }}
            />
          </Field>
        </YStack>
        <YStack {...timeCol} gap={6}>
          <Field label={t({ ru: 'Время', en: 'Time' })} required>
            <TimeField value={fromTime} onChange={onFromTime} maxHour={endHour} highlight={!fromTime} align="right" />
          </Field>
        </YStack>
      </XStack>

      {/* end date + time */}
      <XStack gap={12} flexWrap="wrap">
        <YStack {...dateCol} gap={6}>
          <Field label={t({ ru: 'Дата окончания', en: 'End date' })} required>
            {/* Locked mirror of the start date — отгул is always a single day (≤ 8 h). */}
            {/* No `highlight`: this field is derived/disabled, so it never begs for input. */}
            <DateField value={toDate} onChange={() => {}} disabled placeholder="ГГГГ-ММ-ДД" />
            <Text fontSize={11} color="$tertiary">
              {t({ ru: 'Равна дате начала — отгул в один день', en: 'Same as start date — single-day time-off' })}
            </Text>
          </Field>
        </YStack>
        <YStack {...timeCol} gap={6}>
          <Field label={t({ ru: 'Время', en: 'Time' })} required>
            <TimeField value={toTime} onChange={setToTime} after={fromTime} maxHour={endHour} highlight={!toTime} align="right" />
          </Field>
        </YStack>
      </XStack>

      {/* computed duration */}
      {hasDates && (
        <XStack alignItems="center" gap={7}>
          <Icon name="clock" size={14} color={hint ? tone('red').text : '$muted'} />
          <Text fontSize={12} fontWeight="600" color={hint ? tone('red').text : '$muted'}>
            {timeValid
              ? t({ ru: `Длительность: ${fmtNum(duration)} ч`, en: `Duration: ${fmtNum(duration)} h`, az: `Müddət: ${fmtNum(duration)} saat` })
              : t({ ru: 'Длительность: —', en: 'Duration: —' })}
          </Text>
        </XStack>
      )}

      {/* reason — values from GET /reasons (send the string, not the index) */}
      <Field label={t({ ru: 'Причина', en: 'Reason' })} required>
        <XStack gap={8} flexWrap="wrap">
          {/* While no reason is chosen the whole group carries the navy accent
              (a clear "pick one" cue); once one is set, the rest calm down → dim. */}
          {/* td(): значение (RU-строка из 1С) уходит на бэк как есть, переводится только показ. */}
          {reasons.map((r) => (
            <Chip key={r} on={reason === r} dim={!!reason} onPress={() => setReason(r)}>
              {td(r)}
            </Chip>
          ))}
        </XStack>
      </Field>

      {/* comment ≤ 500 */}
      <Field label={t({ ru: 'Комментарий (необязательно)', en: 'Comment (optional)' })}>
        <FormTextArea
          value={comment}
          onChangeText={setComment}
          maxLength={500}
          placeholder={t({ ru: 'Например: визит к врачу', en: 'e.g. doctor appointment' })}
        />
      </Field>

      {/* inline client-side hint (kept distinct from the server error below) */}
      {hint !== '' && (
        <Text fontSize={12} color={tone('red').text}>
          {hint}
        </Text>
      )}

      <RoutePreview type="TIME_OFF" onBehalfOfUserId={onBehalf?.id} employmentId={employment.employmentId} enabled={employment.ready} />
      <FormActions onCancel={onDone} onSubmit={submit} ok={ok} m={create} missing={missing} />
    </YStack>
  )
}
